import { useState, useCallback, useRef, useEffect } from "react";
import { DraggableImage } from "./DraggableImage";
import { DraggableNote } from "./DraggableNote";

type StoryboardImage = {
  id: string;
  imageUrl: string;
  caption: string | null;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  zIndex: number | null;
};

type StoryboardNote = {
  id: string;
  content: string;
  x: number;
  y: number;
  width: number | null;
  zIndex: number | null;
  color: string | null;
};

type Props = {
  images: StoryboardImage[];
  notes: StoryboardNote[];
  zoom: number;
  setZoom: (zoom: number | ((z: number) => number)) => void;
  canEdit: boolean;
  onAddNote: (x: number, y: number) => void;
  onUpdateImage: (id: string, updates: Partial<StoryboardImage>) => void;
  onUpdateNote: (id: string, updates: Partial<StoryboardNote>) => void;
  onDeleteImage: (id: string) => void;
  onDeleteNote: (id: string) => void;
  onBringToFront: (type: "image" | "note", id: string) => void;
  onUploadImages: (files: FileList | File[], dropPosition?: { x: number; y: number }) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onDeleteSelected?: () => void;
  onImageDoubleClick?: (imageId: string) => void;
};

export function StoryboardCanvas({
  images,
  notes,
  zoom,
  setZoom,
  canEdit,
  onAddNote,
  onUpdateImage,
  onUpdateNote,
  onDeleteImage,
  onDeleteNote,
  onBringToFront,
  onUploadImages,
  onSelectionChange,
  onDeleteSelected,
  onImageDoubleClick,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ mouseX: 0, mouseY: 0, panX: 0, panY: 0 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Box selection state
  const [isBoxSelecting, setIsBoxSelecting] = useState(false);
  const [boxStart, setBoxStart] = useState({ x: 0, y: 0 });
  const [boxEnd, setBoxEnd] = useState({ x: 0, y: 0 });

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  // Handle Delete key to delete selected items
  useEffect(() => {
    if (!canEdit) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't trigger if user is typing in an input
        if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
          return;
        }
        if (selectedIds.size > 0 && onDeleteSelected) {
          e.preventDefault();
          onDeleteSelected();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canEdit, selectedIds, onDeleteSelected]);

  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Point on canvas before zoom (in canvas coordinates)
    const canvasX = (mouseX - pan.x) / zoom;
    const canvasY = (mouseY - pan.y) / zoom;

    // Calculate new zoom
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(3, zoom * zoomFactor));

    // Adjust pan to keep the point under cursor stationary
    const newPanX = mouseX - canvasX * newZoom;
    const newPanY = mouseY - canvasY * newZoom;

    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan, setZoom]);

  // Handle pan (drag empty canvas) or box selection (shift+drag)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start if clicking on an interactive item (image, note, button, input, etc.)
    const target = e.target as HTMLElement;
    if (target.closest('.draggable-item, button, input, textarea, .resize-handle')) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Shift+drag = box selection
    if (e.shiftKey && canEdit) {
      e.preventDefault();
      setIsBoxSelecting(true);
      setBoxStart({ x: mouseX, y: mouseY });
      setBoxEnd({ x: mouseX, y: mouseY });
      // Clear selection unless Ctrl is held (to add to selection)
      if (!e.ctrlKey && !e.metaKey) {
        setSelectedIds(new Set());
      }
    } else {
      // Regular drag = pan
      setSelectedIds(new Set());
      setIsPanning(true);
      setPanStart({
        mouseX: e.clientX,
        mouseY: e.clientY,
        panX: pan.x,
        panY: pan.y,
      });
    }
  }, [pan, canEdit]);

  useEffect(() => {
    if (!isPanning) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - panStart.mouseX;
      const dy = e.clientY - panStart.mouseY;
      setPan({
        x: panStart.panX + dx,
        y: panStart.panY + dy,
      });
    };

    const handleMouseUp = () => {
      setIsPanning(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, panStart]);

  // Box selection mouse tracking
  useEffect(() => {
    if (!isBoxSelecting) return;

    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      setBoxEnd({ x: mouseX, y: mouseY });
    };

    const handleMouseUp = () => {
      // Calculate selection box in canvas coordinates
      const minX = (Math.min(boxStart.x, boxEnd.x) - pan.x) / zoom;
      const maxX = (Math.max(boxStart.x, boxEnd.x) - pan.x) / zoom;
      const minY = (Math.min(boxStart.y, boxEnd.y) - pan.y) / zoom;
      const maxY = (Math.max(boxStart.y, boxEnd.y) - pan.y) / zoom;

      // Find items within the selection box
      const newSelectedIds = new Set(selectedIds);

      images.forEach((img) => {
        const imgX = img.x ?? 0;
        const imgY = img.y ?? 0;
        const imgWidth = img.width ?? 200;
        const imgHeight = img.height ?? 150;

        // Check if image intersects with selection box
        if (
          imgX < maxX &&
          imgX + imgWidth > minX &&
          imgY < maxY &&
          imgY + imgHeight > minY
        ) {
          newSelectedIds.add(img.id);
        }
      });

      notes.forEach((note) => {
        const noteWidth = note.width ?? 200;
        const noteHeight = 100; // Approximate note height

        if (
          note.x < maxX &&
          note.x + noteWidth > minX &&
          note.y < maxY &&
          note.y + noteHeight > minY
        ) {
          newSelectedIds.add(note.id);
        }
      });

      setSelectedIds(newSelectedIds);
      setIsBoxSelecting(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isBoxSelecting, boxStart, boxEnd, pan, zoom, images, notes, selectedIds]);

  // Handle double-click to add note
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!canEdit) return;
    // Only add note if double-clicking on the canvas itself
    if (e.target !== containerRef.current && e.target !== containerRef.current?.firstChild) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Convert screen coordinates to canvas coordinates
    const canvasX = (mouseX - pan.x) / zoom;
    const canvasY = (mouseY - pan.y) / zoom;

    onAddNote(canvasX, canvasY);
  }, [canEdit, pan, zoom, onAddNote]);

  // Handle drag and drop for images
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canEdit) {
      setIsDraggingOver(true);
    }
  }, [canEdit]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (!canEdit) return;

    const container = containerRef.current;
    if (!container) return;

    // Calculate drop position in canvas coordinates
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const canvasX = (mouseX - pan.x) / zoom;
    const canvasY = (mouseY - pan.y) / zoom;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const imageFiles = Array.from(files).filter((f) =>
        ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(f.type)
      );
      if (imageFiles.length > 0) {
        onUploadImages(imageFiles, { x: canvasX, y: canvasY });
      }
    }
  }, [canEdit, onUploadImages, pan, zoom]);

  // Click on canvas to deselect
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (e.target === containerRef.current || e.target === containerRef.current?.firstChild) {
      setSelectedIds(new Set());
    }
  }, []);

  // Handle item selection (supports Shift for multi-select)
  const handleItemSelect = useCallback((id: string, addToSelection?: boolean) => {
    if (addToSelection) {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    } else {
      setSelectedIds(new Set([id]));
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={`flex-1 overflow-hidden bg-surface relative ${isPanning ? 'cursor-grabbing' : 'cursor-grab'} ${
        isDraggingOver ? 'ring-2 ring-inset ring-primary-500' : ''
      }`}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Grid pattern background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(to right, #333 1px, transparent 1px),
            linear-gradient(to bottom, #333 1px, transparent 1px)
          `,
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${pan.x % (20 * zoom)}px ${pan.y % (20 * zoom)}px`,
        }}
      />

      {/* Transform container for pan/zoom */}
      <div
        className="absolute origin-top-left pointer-events-none"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      >
        {/* Render images */}
        {images.map((image) => (
          <div key={image.id} className="pointer-events-auto">
            <DraggableImage
              id={image.id}
              imageUrl={image.imageUrl}
              caption={image.caption}
              x={image.x ?? 0}
              y={image.y ?? 0}
              width={image.width ?? 200}
              height={image.height ?? 150}
              zIndex={image.zIndex ?? 0}
              isSelected={selectedIds.has(image.id)}
              canEdit={canEdit}
              zoom={zoom}
              onSelect={() => handleItemSelect(image.id)}
              onUpdate={(updates) => onUpdateImage(image.id, updates)}
              onDelete={() => onDeleteImage(image.id)}
              onBringToFront={() => onBringToFront("image", image.id)}
              onDoubleClick={() => onImageDoubleClick?.(image.id)}
            />
          </div>
        ))}

        {/* Render notes */}
        {notes.map((note) => (
          <div key={note.id} className="pointer-events-auto">
            <DraggableNote
              id={note.id}
              content={note.content}
              x={note.x}
              y={note.y}
              width={note.width ?? 200}
              zIndex={note.zIndex ?? 0}
              color={note.color ?? "#fbbf24"}
              isSelected={selectedIds.has(note.id)}
              canEdit={canEdit}
              zoom={zoom}
              isNew={note.content === ""}
              onSelect={() => handleItemSelect(note.id)}
              onUpdate={(updates) => onUpdateNote(note.id, updates)}
              onDelete={() => onDeleteNote(note.id)}
              onBringToFront={() => onBringToFront("note", note.id)}
            />
          </div>
        ))}
      </div>

      {/* Box selection rectangle */}
      {isBoxSelecting && (
        <div
          className="absolute border-2 border-primary-500 bg-primary-500/10 pointer-events-none"
          style={{
            left: Math.min(boxStart.x, boxEnd.x),
            top: Math.min(boxStart.y, boxEnd.y),
            width: Math.abs(boxEnd.x - boxStart.x),
            height: Math.abs(boxEnd.y - boxStart.y),
          }}
        />
      )}

      {/* Empty state */}
      {images.length === 0 && notes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm mb-2">No storyboard content yet</p>
            {canEdit && (
              <p className="text-xs">
                Drag images here, use the toolbar to upload,
                <br />
                or double-click to add text
              </p>
            )}
          </div>
        </div>
      )}

      {/* Drag overlay */}
      {isDraggingOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-500/10 pointer-events-none">
          <div className="text-primary-400 text-lg font-medium">Drop images here</div>
        </div>
      )}
    </div>
  );
}
